import React from 'react';
import { formatRate } from '~containers/analytics/kpis/format-rate';
import {
    BLUE_700,
    GRAY_400,
    GRAY_500,
    ORANGE_600,
    PINK_700,
} from '~styles/colors';
import style from './split-bar.module.scss';

/**
 * In this order, never cycled. Known parts past these colors fold into one
 * "Other" part.
 */
const PART_COLORS = [BLUE_700, ORANGE_600, PINK_700];

export interface SplitPart {
    key: string;
    label: string;
    value: number;
    /** A part nobody declared: drawn grey, after the known parts. */
    unknown?: boolean;
}

/**
 * One bar cut into the parts of a whole, with a legend that writes out each
 * part's count and share.
 */
export const SplitBar = ({
    parts,
    empty,
}: {
    parts: SplitPart[];
    empty: string;
}) => {
    const whole = parts.reduce((total, part) => total + part.value, 0);
    if (whole === 0) {
        return <p className={style.empty}>{empty}</p>;
    }
    const known = parts.filter((part) => !part.unknown);
    const folded = known.slice(PART_COLORS.length);
    const colored = [
        ...known
            .slice(0, PART_COLORS.length)
            .map((part, index) => ({ ...part, color: PART_COLORS[index] })),
        ...(folded.length > 0
            ? [
                  {
                      key: 'other',
                      label: 'Other',
                      value: folded.reduce(
                          (total, part) => total + part.value,
                          0
                      ),
                      color: GRAY_500,
                  },
              ]
            : []),
        ...parts
            .filter((part) => part.unknown)
            .map((part) => ({ ...part, color: GRAY_400 })),
    ].filter((part) => part.value > 0);
    return (
        <div className={style.split}>
            <div className={style.bar} aria-hidden="true">
                {colored.map((part) => (
                    <div
                        key={part.key}
                        className={style.segment}
                        style={{
                            flexGrow: part.value,
                            background: part.color,
                        }}
                    />
                ))}
            </div>
            <ul className={style.legend}>
                {colored.map((part) => (
                    <li key={part.key} className={style.legend_item}>
                        <span
                            className={style.swatch}
                            style={{ background: part.color }}
                        />
                        <span className={style.legend_label}>{part.label}</span>
                        <span className={style.legend_value}>
                            {part.value.toLocaleString()}
                        </span>
                        <span className={style.legend_share}>
                            {formatRate(part.value / whole)}
                        </span>
                    </li>
                ))}
            </ul>
        </div>
    );
};
